import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import { getPEMarkets } from '@/services/marketIntelService';
import type { PEMarketsItem } from '@/types/pe.types';
import ChartDateLimiter from '@/components/charts/ChartDateLimiter';
import { useRsColors } from '@/hooks/useRsColors';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { useTheme } from '@/contexts/ThemeContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const SEGMENT_COLORS = [
  'rgba(147,210,255,0.85)', // light blue
  'rgba(37,99,235,0.85)',   // dark blue
  'rgba(249,115,22,0.85)',  // orange
  'rgba(236,72,153,0.85)',  // pink
  'rgba(168,85,247,0.85)',  // purple
  'rgba(109,40,217,0.85)',  // dark purple
];

type ResinType = 'hdpe' | 'ldpe' | 'lldpe';

interface PEMarketsTabProps {
  dateFrom?: string;
  dateTo?: string;
}

const SEGMENT_CONFIG: Record<ResinType,{ keys: (keyof PEMarketsItem)[]; labels: string[]; } > = {
  hdpe: {
    keys: [
      'blow_molding',
      'pipe_conduit',
      'injection_molding',
      'other_segments',
      'film',
      'liquid_food_bottles',
    ],
    labels: [
      'Blow Molding',
      'Pipe & Conduit',
      'Injection Molding',
      'Non-Corrugated',
      'Film (12 mils & Below)',
      'Liquid Food Bottles',
    ],
  },
  ldpe: {
    keys: [
      'film',
      'food_packaging_film',
      'extrusion_coating',
      'non_packaging_film',
      'all_other_non_food_packaging',
      'non_food_packaging_film',
    ],
    labels: [
      'Film (12 mils & Below)',
      'Food Packaging Film',
      'Extrusion Coating',
      'Non-Packaging Film',
      'All Other Non-Food Packaging',
      'Non-Food Packaging Film',
    ],
  },
  lldpe: {
    keys: [
      'film',
      'packaging_film',
      'non_packaging_film',
      'trash_and_can_liners',
      'food_packaging_film',
      'shrink_stretch_film_total',
    ],
    labels: [
      'Film (12 mils & Below)',
      'Packaging Film',
      'Non-Packaging Film',
      'Trash & Can Liners',
      'Food Packaging Film',
      'Shrink/Stretch Film Total',
    ],
  },
};

const PEMarketsTab: React.FC<PEMarketsTabProps> = ({ dateFrom, dateTo }) => {
  const { isDark } = useTheme();
  const [data, setData] = useState<PEMarketsItem[]>([]);
  const [resinType, setResinType] = useState<ResinType>('hdpe');
  const [loading, setLoading] = useState(true);
  // Below 1024px, hide the canvas legend and show a 2-column legend grid instead.
  const [isCompactLegend, setIsCompactLegend] = useState(false);

  const hasFetched = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    setIsCompactLegend(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCompactLegend(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    hasFetched.current = false;
  }, [resinType, dateFrom, dateTo]);

  useEffect(() => {
    if (hasFetched.current) return;

    hasFetched.current = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        const result = await getPEMarkets(resinType, dateFrom, dateTo);
        setData(Array.isArray(result) ? result : []);
      } catch (err) {
        console.error('PEMarketsTab fetch error', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [resinType, dateFrom, dateTo]);

  const labels = data.map((d) => {
    try {
      return format(new Date(d.date + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return d.date;
    }
  });

  const currentConfig = SEGMENT_CONFIG[resinType];

  const allDatasets = currentConfig.keys.map((key, i) => ({
    label: currentConfig.labels[i],

    data: data.map((d) => {
      const val = d[key];
      return val !== null && val !== undefined ? Number(val) : 0;
    }),
    backgroundColor: SEGMENT_COLORS[i],
    borderRadius: 0,
    stack: 'stack',
  }));

  const shouldShowEvery3Months = (label: string, index: number) => {
    try {
      const date = new Date(label);

      if (isNaN(date.getTime())) {
        return index % 3 === 0;
      }

      return date.getMonth() % 6 === 0;
    } catch {
      return index % 3 === 0;
    }
  };

  const rs = useRsColors();
  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);

  const getChartOptions = (showLabels: boolean, chartLabels: string[]): any => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !isCompactLegend,
        position: 'top' as const,
        labels: {
          color: rs.offWhite,
          font: { size: 11 },
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          title: (items: any[]) => {
            const index = items?.[0]?.dataIndex;
            const label = chartLabels?.[index];

            if (!label) return '';

            try {
              return format(new Date(label + 'T00:00:00'), 'MMM yyyy');
            } catch {
              return label;
            }
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: rs.offWhite,
          font: {
            size: 12.5,
          },
          callback: function (_value: any, index: number) {
            const label = chartLabels[index];

            if (!label) return '';

            return shouldShowEvery3Months(label, index) ? label : '';
          },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
        },
        grid: {
          color: rs.gridLine,
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: rs.offWhite,
        },
        grid: {
          color: rs.gridLine,
        },
      },
    },
  });

  return (
    <div className={`flex flex-col gap-4 ${isCompactLegend ? 'p-0 -mx-6' : 'p-4'}`}>
      {/* Resin type selector — centered under 1100px */}
      <div className={`flex gap-2 ${isCompactLegend ? 'justify-center' : ''}`}>
        {(['hdpe', 'ldpe', 'lldpe'] as ResinType[]).map((r) => (
          <button
            key={r}
            onClick={() => setResinType(r)}
            data-testid={`markets-resin-${r}`}
            className={`px-4 py-1.5 rounded text-sm font-medium uppercase transition-colors ${
              resinType === r
                ? 'bg-rs-green text-black'
                : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      <div className={`bg-rs-off-black rounded-lg ${isCompactLegend ? 'p-0' : 'p-4'}`}>
        <h3 className={`text-rs-off-white font-semibold mb-3 text-sm ${isCompactLegend ? 'px-1 pt-2 text-center' : ''}`}>
          {resinType.toUpperCase()} Market Segments
        </h3>

        {/* Custom 2-column legend grid (shown under 1100px) */}
        {!loading && isCompactLegend && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mb-3 w-fit mx-auto">
            {allDatasets.map((ds) => (
              <div key={ds.label} className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: ds.backgroundColor as string }}
                />
                <span className="text-rs-off-white text-xs whitespace-nowrap">{ds.label}</span>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-[700px] text-rs-off-white/50">
            Loading market data…
          </div>
        ) : (
          <ChartDateLimiter labels={labels} datasets={allDatasets}>
            {(limitedLabels, limitedDatasets) => (
              <div className="h-[700px]" data-testid="markets-chart">
                <Bar
                  data={{
                    labels: limitedLabels,
                    datasets: limitedDatasets,
                  }}
                  options={getChartOptions(
                    limitedLabels.length <= 13,
                    limitedLabels
                  )}
                  plugins={[hoverLinePlugin]}
                />
              </div>
            )}
          </ChartDateLimiter>
        )}
      </div>

      <p className={`text-rs-highlight-gray text-xs ${isCompactLegend ? 'px-4' : ''}`}>
        Source: American Chemistry Council® (ACC)
      </p>
    </div>
  );
};

export default PEMarketsTab;